/**
 * Nursing homes domain — utility functions and shared types.
 *
 * Re-exports generated entity types plus the typed adapters that bridge the
 * Kubb-generated shapes to the legacy props expected by `DECommonTable`,
 * `DemographicsSection`, etc. The cross-domain adapters live here (rather
 * than in component files) so the casts are documented and centralized.
 */

import type { NursingHomeChainProxy } from '../../models/nursinghome'
import type {
  StandardPaginatedResponse,
  StandardSummaryResponse,
} from '../../models/paginated-response'
import type { AddressInfo } from '../../models/restaurant'
import { getNursingHomesSummary as getNursingHomesSummaryClient } from '../generated/clients/getNursingHomesSummary'
import { listNursingHomes as listNursingHomesClient } from '../generated/clients/listNursingHomes'
import type { GetNursingHomesSummaryQueryParams } from '../generated/types/GetNursingHomesSummary'
import type { ListNursingHomesQueryParams } from '../generated/types/ListNursingHomes'
import type { NursingHomeNested } from '../generated/types/NursingHomeNested'

// ── Domain entity type re-exports ───────────────────────────────────

export type { NursingHomeListItem } from '../generated/types/NursingHomeListItem'
export type { NursingHomeNested } from '../generated/types/NursingHomeNested'
export type { PaginatedNursingHomeListItemList } from '../generated/types/PaginatedNursingHomeListItemList'

// ── Typed adapters for legacy component props ───────────────────────

/**
 * List nursing homes shaped for `DECommonTable.apiGetListAction`.
 *
 * The table component is generic over `NursingHomeChainProxy` (the legacy
 * model the column definitions are typed against). At runtime the generated
 * `NursingHomeListItem` has the same fields the columns read, so the cast is
 * a type-only bridge — encapsulated here so call sites stay clean.
 */
export async function listNursingHomesForTable(
  params: ListNursingHomesQueryParams = {}
): Promise<StandardPaginatedResponse<NursingHomeChainProxy>> {
  return listNursingHomesClient(
    params
  ) as unknown as StandardPaginatedResponse<NursingHomeChainProxy>
}

/**
 * Get the nursing homes summary count shaped for
 * `DECommonTable.apiGetSummaryAction`.
 *
 * The legacy `StandardSummaryResponse` has a second `alt_count` field the
 * nursing-homes backend never populates; the table treats the missing value
 * as `undefined`. We build the response explicitly and default `alt_count`
 * to 0 — the same value the shared table plumbing falls back to.
 */
export async function getNursingHomesSummaryForTable(
  params: GetNursingHomesSummaryQueryParams = {}
): Promise<StandardSummaryResponse> {
  const res = await getNursingHomesSummaryClient(params)
  return { count: res.count, alt_count: 0 }
}

/**
 * Convert the generated `NursingHomeNested.address_info` (a `VerticalZipCode`
 * shape) into the legacy `AddressInfo` interface that `DemographicsSection`
 * accepts. The runtime data is compatible; this helper makes the type bridge
 * explicit instead of repeating the cast at each call site.
 */
export function toLegacyAddressInfo(
  addressInfo: NursingHomeNested['address_info'] | null | undefined
): AddressInfo | undefined {
  if (!addressInfo) return undefined
  return addressInfo as AddressInfo
}
